import { useEffect, useState } from "react";
import { theme, FONT_RUNE } from "./theme";
import { useIsWide } from "./useIsWide";

/**
 * "The Story Behind The Game" — a narrative drawer / sheet that tells the historical arc
 * from Pythagoras through Boethius to medieval cloister tables. Same drawer pattern as
 * Instructions (left on wide, bottom-sheet on narrow) so the interaction model stays
 * consistent across the home screen's three info surfaces.
 */
export function StoryPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [closing, setClosing] = useState(false);
  const isWide = useIsWide();

  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 180);
  };

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(8, 5, 3, 0.7)",
    zIndex: 100,
    display: "flex",
    alignItems: isWide ? "stretch" : "flex-end",
    justifyContent: isWide ? "flex-start" : "center",
    backdropFilter: "blur(2px)",
  };

  const panelStyle: React.CSSProperties = isWide
    ? {
        width: "100%",
        maxWidth: 540,
        height: "100%",
        background: theme.bgElev,
        borderRight: `1px solid ${theme.accent}`,
        display: "flex",
        flexDirection: "column",
        transform: closing ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 200ms ease",
        animation: closing ? "" : "rithmoStoryIn 220ms ease",
        boxShadow: "8px 0 22px rgba(0,0,0,0.5)",
        paddingTop: "var(--safe-top)",
        paddingBottom: "var(--safe-bottom)",
      }
    : {
        width: "100%",
        maxWidth: 680,
        maxHeight: "calc(92dvh - var(--safe-bottom))",
        background: theme.bgElev,
        borderTop: `1px solid ${theme.accent}`,
        borderRadius: "10px 10px 0 0",
        display: "flex",
        flexDirection: "column",
        transform: closing ? "translateY(100%)" : "translateY(0)",
        transition: "transform 200ms ease",
        animation: closing ? "" : "rithmoStoryUp 220ms ease",
        paddingBottom: "var(--safe-bottom)",
      };

  return (
    <div onClick={handleClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px 10px",
            borderBottom: `1px solid ${theme.boardEdge}`,
            background: theme.bgElev,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-script)",
              fontSize: 36,
              lineHeight: 1,
              color: theme.parchment,
            }}
          >
            The story behind the game
          </div>
          <button
            onClick={handleClose}
            style={{
              fontSize: 20,
              padding: "4px 12px",
              background: theme.boardEdge,
              color: theme.parchment,
              border: `1px solid ${theme.inkSoft}`,
              borderRadius: 6,
              fontFamily: FONT_RUNE,
              cursor: "pointer",
              lineHeight: 1,
            }}
            aria-label="Close story"
          >
            ×
          </button>
        </div>

        <div
          style={{
            overflowY: "auto",
            padding: "18px 22px 28px",
            color: theme.parchment,
            fontSize: 15,
            lineHeight: 1.65,
          }}
        >
          <p style={dropCap}>
            Twenty-six centuries ago, the philosopher Pythagoras was walking past a blacksmith's
            shop on the island of Samos when something made him stop. The hammers, striking iron in
            different rhythms, were producing tones — and some pairs of tones sounded right
            together, while others sounded sour. He went inside, weighed the hammers, and found
            something remarkable: the pairs that sounded good had weights in simple integer ratios.
            2 to 1. 3 to 2. 4 to 3. The ugly pairs were everything else.
          </p>

          <p style={paragraph}>
            The forge story is probably apocryphal. But the observation Pythagoras's school built
            from it was real. They stretched strings of different lengths and discovered that
            musical consonance is <i>literally</i> arithmetic. Halve a string, you hear an octave.
            Make it two-thirds the length, a perfect fifth. Three-quarters, a perfect fourth. Music
            wasn't an art form floating loose from mathematics — it <i>was</i> mathematics,
            vibrating.
          </p>

          <p style={paragraph}>
            This was the founding insight of the Pythagorean school, and they took it further than
            anyone before them: they came to believe that all of reality, not just music, was
            structured by number. The orbits of the planets. The proportions of the human body.
            The harmony of the soul itself. Everything, they said, ran on the same arithmetic.
            They called this conviction <span style={accent}>the music of the spheres</span>.
          </p>

          <p style={paragraph}>
            Out of this came the <b>three means</b> — three different ways of finding "the middle"
            between two numbers, each with a physical meaning on a vibrating string. The{" "}
            <i>arithmetic</i> mean of two string lengths gives you a perfect fourth between them.
            The <i>harmonic</i> mean gives you a perfect fifth. The <i>geometric</i> mean falls
            between the two, on a tone that — as it would turn out twenty-three centuries later —
            would become the equal-tempered tritone of modern Western music. The Greeks named the
            second one harmonic precisely because it produced the <i>harmonia</i>: the dominant
            interval, the most stable consonance after the octave itself.
          </p>

          <p style={paragraph}>A thousand years pass.</p>

          <p style={paragraph}>
            Around 500 AD, a Roman senator named Boethius — about to be executed for treason — used
            his final months in prison to write a textbook on arithmetic. He wanted to preserve,
            for whoever survived the falling empire, what the Greeks had figured out about numbers
            and music. His book, <i>De Institutione Arithmetica</i>, became the standard
            mathematics curriculum across medieval Europe for the next thousand years. To study
            arithmetic in any cathedral school from Charlemagne's court onward was, essentially, to
            study Boethius.
          </p>

          <p style={paragraph}>
            Five centuries after that, somewhere in a German monastery around 1030 AD, an abbot
            named <span style={accent}>Asilo of Würzburg</span> invented a game.
          </p>

          <p style={paragraph}>
            He took the proportions from Boethius — every meaningful ratio of integers, every
            progression, every classical mean — and embedded them into the pieces of a board.
            Rounds, Triangles, and Squares, each labeled with a number derived from a Pythagorean
            operation. To win, you had to demonstrate that you understood the relationships
            between them. You had to <i>play</i> the arithmetic. He called it{" "}
            <span style={accent}>Rithmomachia</span> — the battle of numbers.
          </p>

          <p style={paragraph}>
            For five hundred years, every educated person in Europe played this game. It was on the
            curriculum at Oxford and Paris alongside Latin, logic, and astronomy. Bishops played
            it. Kings played it. Chess was for soldiers — Rithmomachia was for philosophers.
          </p>

          <p style={paragraph}>
            Then the Renaissance arrived. Arabic algebra replaced Pythagorean ratios as the
            foundation of mathematics, calculus pushed past it, and the game's pedagogical purpose
            evaporated. By 1600 almost nobody played it anymore. It survived only in scattered
            manuscripts, footnotes in the history of mathematics, and the occasional reconstructed
            board in a museum.
          </p>

          <p style={paragraph}>
            When you play here, you're touching something very old. The piece values you'll move
            around — <span style={accent}>3, 4, 6, 9</span> — are the same numbers Pythagoras's
            students stopped on a monochord twenty-five centuries ago to demonstrate the perfect
            fifth. The Glorious Victory that needs all three means at once isn't a game designer's
            invention; it's the Pythagorean dream made into a problem you can solve: prove that a
            single set of numbers can satisfy all three classical proportions simultaneously. They
            believed the universe ran on patterns like this.
          </p>

          <p style={{ ...paragraph, marginTop: 22, color: theme.accentSoft, fontStyle: "italic" }}>
            Maybe it does.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes rithmoStoryUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes rithmoStoryIn {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

const paragraph: React.CSSProperties = {
  margin: "0 0 14px",
};

const dropCap: React.CSSProperties = {
  ...paragraph,
  marginTop: 0,
};

const accent: React.CSSProperties = {
  color: theme.accentSoft,
  fontFamily: FONT_RUNE,
  letterSpacing: 0.5,
};
